import FlagIcon from "@mui/icons-material/Flag";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom-v5-compat";
import { Loading } from "shared-ui/global/Loading";
import { useOrgQuery } from "../../../../types";
import BulkAssignRoleModal from "../../../../views/Staff/Org/BulkAssignRoleModal";
import CreateUserModal from "../../../../views/Staff/Org/CreateModal";
import { EditOrgMembershipDialog } from "../../../../views/Staff/Org/EditDialog";
import { OrgFeatureFlagsDialog } from "../../../../views/Staff/Org/FeatureFlagsDialog";
import { AdminOrgMembershipTableRow, formatAxleNameList, OrgMembershipTable } from "../../../../views/Staff/Org/Table";

export const StaffOrgPage = () => {
  const { orgId: maybeOrgId } = useParams();
  const id = maybeOrgId || "";
  const [queryText, setQueryText] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [featureFlagsOpen, setFeatureFlagsOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedOrgMembership, setSelectedOrgMembership] = useState<AdminOrgMembershipTableRow | null>(null);
  const { data, loading, refetch } = useOrgQuery({ variables: { id } });

  if (loading || !data) {
    return <Loading progressSize="lg" />;
  }
  const org = data.org;

  // Map every membership to a row; selection resolves against the full set so it survives filtering.
  const allRows: AdminOrgMembershipTableRow[] = org.memberships.map(({ roles, user, ...rest }) => ({
    ...user,
    ...rest,
    roles: [...roles],
    userTypeDisplay: formatAxleNameList(rest.axleUserTypeNames),
    teamDisplay: formatAxleNameList(rest.axleTeamNames),
  }));

  const lowercaseQuery = queryText.toLowerCase();
  const displayRows = allRows.filter(row =>
    `${row.firstName} ${row.lastName} ${row.email} ${row.username} ${row.phoneNumber} ${row.axlePersonDocumentId || ""}`
      .toLowerCase()
      .includes(lowercaseQuery)
  );

  const selectedSet = new Set(selectedIds);
  const selectedMembers = allRows.filter(r => selectedSet.has(r.id));

  const toggleRow = (rowId: string) =>
    setSelectedIds(prev => (prev.includes(rowId) ? prev.filter(x => x !== rowId) : [...prev, rowId]));
  // Select-all applies to the current page's rows only; selections on other pages are preserved.
  const toggleAll = (pageRowIds: string[], checked: boolean) =>
    setSelectedIds(prev => {
      if (checked) {
        return Array.from(new Set([...prev, ...pageRowIds]));
      }
      const pageSet = new Set(pageRowIds);
      return prev.filter(id => !pageSet.has(id));
    });

  const hasTeamworksIdWithSuperuser = org.teamworksId !== null && org.hasTwSuperuserPersonId === true;

  return (
    <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <TextField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          inputProps={{
            "aria-label": `Search Org Memberships`,
          }}
          placeholder={"Search Org Memberships (name, email, username, phone, Axle person ID)"}
          value={queryText}
          onChange={e => setQueryText(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <IconButton onClick={() => setFeatureFlagsOpen(true)} size="large">
          <FlagIcon />
        </IconButton>
      </Box>
      <OrgMembershipTable
        tableRows={displayRows}
        onClickAddEntry={() => setCreateModalOpen(true)}
        onClickRow={(row: AdminOrgMembershipTableRow) => {
          setSelectedOrgMembership(row);
          setEditModalOpen(true);
        }}
        isTeamworksUserManagementEnabled={!!org.isAxleSyncEnabled}
        selectable
        selectedIds={selectedIds}
        onToggleRowSelection={toggleRow}
        onToggleAllSelection={toggleAll}
      />

      {selectedIds.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "grey.900",
            color: "common.white",
            borderRadius: 2,
            px: 1,
            py: 0.5,
            boxShadow: 6,
            zIndex: theme => theme.zIndex.modal - 1,
          }}>
          <Button size="small" onClick={() => setSelectedIds([])} sx={{ color: "common.white", textTransform: "none" }}>
            Clear selected ({selectedIds.length})
          </Button>
          <Box sx={{ width: "1px", height: 24, bgcolor: "grey.700" }} />
          <Button size="small" onClick={() => setBulkModalOpen(true)} sx={{ color: "common.white", textTransform: "none" }}>
            Assign Roles
          </Button>
        </Box>
      )}

      {bulkModalOpen && (
        <BulkAssignRoleModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          orgId={org.id}
          selectedMembers={selectedMembers}
          onAssigned={() => {
            setSelectedIds([]);
            refetch();
          }}
        />
      )}

      {createModalOpen && <CreateUserModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} org={org} />}

      {editModalOpen && selectedOrgMembership && (
        <EditOrgMembershipDialog
          open={editModalOpen}
          onClose={() => {
            setSelectedOrgMembership(null);
            setEditModalOpen(false);
          }}
          orgMembership={selectedOrgMembership}
          org={org}
        />
      )}

      {featureFlagsOpen && (
        <OrgFeatureFlagsDialog
          open={featureFlagsOpen}
          onClose={() => setFeatureFlagsOpen(false)}
          orgId={org.id}
          hasTeamworksIdWithSuperuser={hasTeamworksIdWithSuperuser}
          featureFlags={org.featureFlags}
        />
      )}
    </Box>
  );
};
